import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'

import { apiZodios } from '~api/apiZodios'
import {
  AudioPlayer,
  Banner,
  Button,
  Chip,
  Icon,
  IconButton,
  Switch,
  Typography,
  WhatsAppBubbleIcon,
  formatPhoneNumber,
} from '~components/internal'
import { IS_DEMO, IS_PROD } from '~config'
import { internalLightTheme } from '~createInternalTheme'
import EditQuestionsDialog from '~pages/HealthcheckScreen/components/EditQuestionsDialog/EditQuestionsDialog'
import { type ExpectedSource } from '~pages/HealthcheckScreen/expectedSources'
import { type RootState } from '~redux/rootReducer'
import { lightTokens, typography } from '~tokens/internal'
import {
  type AIInteractionTranscript,
  AIInteractionType,
  type AIPhoneLineTypeEnum,
  type AccessReasonEnum,
  type AgentTraceNode,
  type EnrichedOrganizationAISettings,
  type EnrichedToolCall,
  type IAIInteractionMessageDisplay,
  type IdentityId,
  type UserId,
} from '~types'
import { convertToLogMessages } from '~utils/aiInteractionUtils'
import { resolveContactRouteByIdentity } from '~utils/contactRouteUtils'
import { getBasePath } from '~utils/navigationUtils'

import { getPhoneLineTypeDisplay } from '../../components/AIPhoneLineDialog'
import { MessageBubble } from '../../components/MessageBubble'
import RevealPIIModal from '../../components/RevealPIIModal'
import { ToolCallInfo } from '../../components/ToolCallInfo'
import { getInteractionTypeMeta } from '../../constants/interactionTypeConstants'
import { type AgentGroupItem, AgentToolCallDrawer } from '../AgentToolCallDrawer'
import { AgentTracePanel, splitTraceByQuery } from '../AgentTracePanel'
import { AIInteractionLogEvaluation } from '../AIInteractionLogEvaluation'
import { FromToPhoneChip } from '../FromToPhoneChip'
import ShareLogModal from '../ShareLogModal'
import SystemPromptModal from '../SystemPromptModal'

import './AIInteractionLogContent.scss'

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

const LLM_MODEL_LABELS: Record<string, string> = {
  'gpt-4o-mini': 'GPT-4o Mini',
  'gpt-4o': 'GPT-4o',
  'gpt-4.1-mini': 'GPT-4.1 Mini',
  'gpt-5': 'GPT-5',
  'gpt-5-mini': 'GPT-5 Mini',
  'gpt-5.1': 'GPT-5.1',
  'gpt-5.2': 'GPT-5.2',
  'gpt-5.4': 'GPT-5.4',
  'gpt-5.4-mini': 'GPT-5.4 Mini',
  'gpt-5.4-nano': 'GPT-5.4 Nano',
  'o3-mini': 'O3 Mini',
  'claude-haiku-4-5-20251001': 'Claude 4.5 Haiku',
  'claude-sonnet-4-5-20250929': 'Claude 4.5 Sonnet',
  'claude-opus-4-5-20251101': 'Claude 4.5 Opus',
  'us.anthropic.claude-haiku-4-5-20251001-v1:0': 'Claude 4.5 Haiku',
  'us.anthropic.claude-sonnet-4-6': 'Claude 4.6 Sonnet',
  'us.anthropic.claude-opus-4-8': 'Claude 4.8 Opus',
  'gemini-2.0-flash-lite': 'Gemini 2.0 Flash Lite',
  'gemini-2.5-flash': 'Gemini 2.5 Flash',
  'gemini-2.5-flash-lite': 'Gemini 2.5 Flash Lite',
  'gemini-2.5-pro': 'Gemini 2.5 Pro',
  'gemini-3-flash-preview': 'Gemini 3 Flash',
  'gemini-3-pro-preview': 'Gemini 3 Pro',
  'gemini-3.1-flash-lite': 'Gemini 3.1 Flash Lite',
  'gemini-3.5-flash-lite': 'Gemini 3.5 Flash Lite',
}

const getLLMProviderLabel = (model: string): string => {
  if (model.startsWith('claude') || model.includes('anthropic')) return 'Anthropic'
  if (model.startsWith('gemini')) return 'Google'
  return 'OpenAI'
}

const getLLMModelLabel = (model: string): string => LLM_MODEL_LABELS[model] ?? model

interface AIInteractionLogContentProps {
  aiInteractionId: string
  onClose?: () => void
  onError?: () => void
  onTestStatusChange?: (aiInteractionId: string, isTest: boolean) => void
  showHeader?: boolean
  showOrgInfo?: boolean
  initialOrgAiSettings?: EnrichedOrganizationAISettings | null
  debugToggleMode?: 'default' | 'off' | 'floating'
  organizationIdentityId?: string | null
  scrollToMessageId?: string | null
}

type TimelineItem =
  | { type: 'message'; data: IAIInteractionMessageDisplay; timestamp: Date }
  | { type: 'toolCall'; data: EnrichedToolCall; timestamp: Date }
  | { type: 'agentGroup'; data: AgentGroupItem; timestamp: Date }
  | { type: 'agentTrace'; data: AgentTraceNode[]; timestamp: Date }
  | { type: 'externalApiFailure'; data: string; timestamp: Date }

const AIInteractionLogContent: React.FC<AIInteractionLogContentProps> = ({
  aiInteractionId,
  onClose,
  onError,
  onTestStatusChange,
  showHeader = true,
  showOrgInfo = false,
  initialOrgAiSettings = null,
  debugToggleMode = 'default',
  organizationIdentityId = null,
  scrollToMessageId = null,
}) => {
  const user = useSelector((state: RootState) => state?.auth.user)
  const isSuper = useSelector((state: RootState) => state?.auth?.user?.super)
  const location = useLocation()
  const orgHandleFromUrl = location.pathname.match(/\/organizations\/([^/]+)\//)?.[1] ?? null
  const emailIdentities = useSelector((state: RootState) => state?.organization.emailIdentities)
  const userBelongsToOrg = emailIdentities?.some((ei) => ei.handle === orgHandleFromUrl)
  const navigate = useNavigate()

  const [orgAiSettings, setOrgAiSettings] = useState<EnrichedOrganizationAISettings | null>(
    initialOrgAiSettings ?? null,
  )
  const [useEnglish, setUseEnglish] = useState(false)
  const [isTestingMode, setIsTestingMode] = useState(false)
  const [aiInteractionTranscript, setAiInteractionTranscript] =
    useState<AIInteractionTranscript | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([])
  const [linkCopied, setLinkCopied] = useState<boolean>(false)
  const [isAddTestDialogOpen, setIsAddTestDialogOpen] = useState(false)
  const [addedToTestBank, setAddedToTestBank] = useState(false)
  const [showAddedSuccess, setShowAddedSuccess] = useState(false)
  const [isArchived, setIsArchived] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [debugVisible, setDebugVisible] = useState<boolean>(true)
  const [tracesVisible, setTracesVisible] = useState<boolean>(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false)
  const [piiRevealed, setPiiRevealed] = useState<boolean>(false)
  const [revealLoading, setRevealLoading] = useState<boolean>(false)
  const [revealError, setRevealError] = useState<string | null>(null)
  const [redactedTranscript, setRedactedTranscript] = useState<AIInteractionTranscript | null>(null)
  const [isRevealPIIModalOpen, setIsRevealPIIModalOpen] = useState<boolean>(false)
  const [isSystemPromptModalOpen, setIsSystemPromptModalOpen] = useState<boolean>(false)
  const [scenarioCategories, setScenarioCategories] = useState<string[]>([])
  const [resolvedContactUrl, setResolvedContactUrl] = useState<string | null>(null)

  const showDebugInfo =
    isTestingMode || (isSuper && debugVisible) || (!isSuper && !IS_PROD && !IS_DEMO)
  const showTraces = isSuper && tracesVisible
  const hasAgentTrace =
    !!aiInteractionTranscript?.agent_trace?.length &&
    splitTraceByQuery(aiInteractionTranscript.agent_trace).length > 0

  const contactOrgIdentityId = useMemo(() => {
    if (!orgHandleFromUrl || !emailIdentities) {
      return null
    }
    return (
      emailIdentities.find((emailIdentity) => emailIdentity.handle === orgHandleFromUrl)?.id ?? null
    )
  }, [orgHandleFromUrl, emailIdentities])

  const hasNonEnglishContent = aiInteractionTranscript
    ? aiInteractionTranscript.queries.some((q) => q.language && q.language !== 'en') ||
      aiInteractionTranscript.responses.some((r) => r.language && r.language !== 'en')
    : false

  const handleLanguageChange = (
    _: React.MouseEvent<HTMLElement>,
    newValue: boolean | null,
  ): void => {
    if (newValue !== null) {
      setUseEnglish(newValue)
    }
  }

  const handleCloseAlert = (_event?: React.SyntheticEvent | Event, reason?: string): void => {
    if (reason === 'clickaway') return
    setLinkCopied(false)
    setShowAddedSuccess(false)
  }

  const handleAddTest = async (
    queries: Array<{
      question: string
      expected_answer: string | null
      relevant_sources: ExpectedSource[] | null
    }>,
    category: string | null,
  ): Promise<void> => {
    if (!user || !orgAiSettings || !orgAiSettings.organization_identity_id) return

    try {
      await apiZodios.ai_addScenario({
        user_id: user.id,
        organization_identity_id: orgAiSettings.organization_identity_id,
        queries: queries.map((q) => ({
          question: q.question,
          expected_answer: q.expected_answer ?? undefined,
          relevant_sources: q.relevant_sources ?? undefined,
        })),
        category,
      })
      setIsAddTestDialogOpen(false)
      setAddedToTestBank(true)
      setShowAddedSuccess(true)
    } catch (error) {
      console.error('Error adding test question:', error)
    }
  }

  const handleToggleArchived = async (): Promise<void> => {
    if (!user || !orgAiSettings?.organization_identity_id || !aiInteractionTranscript) return
    setArchiving(true)
    try {
      await apiZodios.ai_markAIInteractionAsTest({
        user_id: user.id,
        organization_identity_id: orgAiSettings.organization_identity_id,
        ai_interaction_id: aiInteractionTranscript.ai_interaction_id,
        is_test: !isArchived,
      })
      const newValue = !isArchived
      setIsArchived(newValue)
      onTestStatusChange?.(aiInteractionTranscript.ai_interaction_id, newValue)
    } catch (error) {
      console.error('Failed to update archive status:', error)
    } finally {
      setArchiving(false)
    }
  }

  const handleOnClose = (): void => {
    setTimelineItems([])
    setAiInteractionTranscript(null)
    setPiiRevealed(false)
    setRevealLoading(false)
    setRevealError(null)
    setRedactedTranscript(null)
    setIsRevealPIIModalOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const fetchAIInteractionTranscript = async (aiInteractionId: string): Promise<void> => {
    setLoading(true)
    try {
      if (!user || !aiInteractionId) return

      const resolvedOrgIdentityId =
        orgAiSettings?.organization_identity_id ?? organizationIdentityId ?? null

      if (!resolvedOrgIdentityId) {
        if (!isSuper) {
          const { organization_handle: organizationHandle } =
            await apiZodios.ai_resolveAIInteractionOrganization({
              user_id: user.id,
              ai_interaction_id: aiInteractionId,
            })
          navigate(`/organizations/${organizationHandle}/ai-log/${aiInteractionId}`, {
            replace: true,
          })
          return
        }
        const response = await apiZodios.ai_superGetAIInteractionTranscript({
          user_id: user.id,
          ai_interaction_id: aiInteractionId,
        })
        setAiInteractionTranscript(response.interaction_transcript)
        setIsArchived(response.interaction_transcript.is_test ?? false)
        setOrgAiSettings(response.organization_ai_settings)
      } else {
        const response = await apiZodios.ai_getAIInteractionTranscript({
          user_id: user.id,
          organization_identity_id: resolvedOrgIdentityId,
          ai_interaction_id: aiInteractionId,
        })
        setAiInteractionTranscript(response.interaction_transcript)
        setIsArchived(response.interaction_transcript.is_test ?? false)
        setOrgAiSettings(response.organization_ai_settings)
      }
    } catch (error) {
      console.error('Failed to fetch AIInteraction details:', error)
      onError?.()
    } finally {
      setLoading(false)
    }
  }

  const getShareableLink = (): string => {
    const baseUrl = window.location.origin
    if (orgHandleFromUrl) {
      return `${baseUrl}${getBasePath()}/organizations/${orgHandleFromUrl}/ai-log/${aiInteractionId}`
    }
    return `${baseUrl}${getBasePath()}/ai-log/${aiInteractionId}`
  }

  const handleOpenShareModal = useCallback((): void => {
    setIsShareModalOpen(true)
  }, [])

  const handleCloseShareModal = useCallback((): void => {
    setIsShareModalOpen(false)
  }, [])

  const handleRevealRedactions = (): void => {
    setIsRevealPIIModalOpen(true)
  }

  const handleConfirmRevealPII = async (reason: AccessReasonEnum): Promise<void> => {
    if (!user || !aiInteractionId || !orgAiSettings?.organization_identity_id) return
    setRevealLoading(true)
    setRevealError(null)
    setIsRevealPIIModalOpen(false)
    try {
      const response = await apiZodios.ai_getAIInteractionPIITranscript({
        user_id: user.id,
        organization_identity_id: orgAiSettings.organization_identity_id,
        ai_interaction_id: aiInteractionId,
        reason,
      })
      setRedactedTranscript(aiInteractionTranscript)
      setAiInteractionTranscript(response.interaction_transcript)
      setPiiRevealed(true)
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status === 403) {
        setRevealError('You do not have permission to view unredacted content.')
      } else {
        setRevealError('Failed to reveal redactions. Please try again.')
      }
      console.error('Failed to reveal redactions:', error)
    } finally {
      setRevealLoading(false)
    }
  }

  const handleRestoreRedactions = (): void => {
    if (redactedTranscript) {
      setAiInteractionTranscript(redactedTranscript)
    }
    setPiiRevealed(false)
    setRedactedTranscript(null)
  }

  useEffect(() => {
    if (orgAiSettings || !orgHandleFromUrl) return
    apiZodios
      .ai_initializeAI({ organization_handle: orgHandleFromUrl })
      .then((settings) => setOrgAiSettings(settings.organization_ai_settings))
      .catch(console.error)
  }, [orgHandleFromUrl])

  useEffect(() => {
    if (!user?.id || !contactOrgIdentityId || !aiInteractionTranscript?.contact_identity_id) {
      setResolvedContactUrl(null)
      return
    }

    let cancelled = false
    void (async (): Promise<void> => {
      try {
        const route = await resolveContactRouteByIdentity(
          user.id as UserId,
          contactOrgIdentityId as IdentityId,
          aiInteractionTranscript.contact_identity_id as IdentityId,
        )
        if (!cancelled) {
          setResolvedContactUrl(route)
        }
      } catch {
        if (!cancelled) {
          setResolvedContactUrl(null)
        }
      }
    })()

    return (): void => {
      cancelled = true
    }
  }, [user?.id, contactOrgIdentityId, aiInteractionTranscript?.contact_identity_id])

  useEffect(() => {
    if (
      orgHandleFromUrl &&
      !userBelongsToOrg &&
      isSuper &&
      emailIdentities &&
      emailIdentities.length > 0
    ) {
      navigate(`/ai-log/${aiInteractionId}`, { replace: true })
    }
  }, [orgHandleFromUrl, userBelongsToOrg, isSuper, aiInteractionId])

  useEffect(() => {
    setAiInteractionTranscript(null)
    setPiiRevealed(false)
    setRevealLoading(false)
    setRevealError(null)
    setRedactedTranscript(null)
    setIsRevealPIIModalOpen(false)
  }, [aiInteractionId])

  useEffect(() => {
    const hasTestingPath = location.pathname.endsWith('/testing')
    setIsTestingMode(hasTestingPath)

    if (orgHandleFromUrl && !orgAiSettings?.organization_identity_id) return
    if (aiInteractionTranscript) return

    fetchAIInteractionTranscript(aiInteractionId)
  }, [location, aiInteractionId, orgAiSettings?.organization_identity_id])

  useEffect(() => {
    if (aiInteractionTranscript) {
      const messageList = convertToLogMessages(aiInteractionTranscript, {
        useEnglish,
      })

      const timeline: TimelineItem[] = []

      messageList.forEach((msg) => {
        timeline.push({
          type: 'message',
          data: msg,
          timestamp: msg.timestamp,
        })
      })

      if (showDebugInfo) {
        for (const query of aiInteractionTranscript.queries) {
          if (query.inbound_policies?.policies.length) {
            timeline.push({
              type: 'agentGroup',
              data: {
                agentLabel: 'Gatekeeper Agent',
                toolCalls: [],
                model: query.inbound_policies.model
                  ? getLLMModelLabel(query.inbound_policies.model)
                  : '',
                policies: query.inbound_policies,
              } satisfies AgentGroupItem,
              timestamp: new Date(query.created),
            })
          }
        }
        for (const toolCall of aiInteractionTranscript.tool_calls ?? []) {
          if (toolCall.external_api_failure) {
            timeline.push({
              type: 'externalApiFailure',
              data: toolCall.external_api_failure,
              timestamp: new Date(toolCall.created),
            })
          }
        }
      }

      const agentTrace = aiInteractionTranscript.agent_trace
      const isMultiAgent =
        !!aiInteractionTranscript.retrieval_model || !!aiInteractionTranscript.generator_model

      if (showTraces && agentTrace && agentTrace.length > 0) {
        // One panel per query: the run that answered it, placed at its own start time.
        for (const queryNodes of splitTraceByQuery(agentTrace)) {
          const startTimes = queryNodes
            .map((node) => (node.started_at ? new Date(node.started_at).getTime() : null))
            .filter((time): time is number => time !== null)
          timeline.push({
            type: 'agentTrace',
            data: queryNodes,
            timestamp: new Date(
              startTimes.length > 0
                ? Math.min(...startTimes)
                : new Date(aiInteractionTranscript.created).getTime(),
            ),
          })
        }
      } else if (showDebugInfo && isMultiAgent) {
        const toolCalls = aiInteractionTranscript.tool_calls ?? []
        const generatorModel =
          aiInteractionTranscript.generator_model ?? aiInteractionTranscript.llm_model ?? ''
        const RETRIEVAL_AGENT_MODEL = aiInteractionTranscript.retrieval_model ?? generatorModel

        // One Retrieval Agent + Generator Agent drawer per turn.
        let prevResponseTime = 0
        for (const response of aiInteractionTranscript.responses) {
          const responseTime = new Date(response.created).getTime()
          const generationStartedAt = response.generation_started_at
            ? new Date(response.generation_started_at).getTime()
            : null
          const turnToolCalls = toolCalls.filter((tc) => {
            const t = new Date(tc.created).getTime()
            return t > prevResponseTime && t < responseTime
          }) as EnrichedToolCall[]

          if (turnToolCalls.length > 0) {
            const firstTcTime = new Date(turnToolCalls[0].created).getTime()
            const retrievalEndTime = generationStartedAt ?? responseTime
            const durationMs = retrievalEndTime - firstTcTime
            timeline.push({
              type: 'agentGroup',
              data: {
                agentLabel: 'Retrieval Agent',
                toolCalls: turnToolCalls,
                model: getLLMModelLabel(RETRIEVAL_AGENT_MODEL),
                durationMs: durationMs > 0 ? durationMs : undefined,
              } satisfies AgentGroupItem,
              timestamp: new Date(turnToolCalls[0].created),
            })
          }

          // Generator Agent drawer for every response (generator always runs)
          if (generationStartedAt) {
            timeline.push({
              type: 'agentGroup',
              data: {
                agentLabel: 'Generator Agent',
                toolCalls: [],
                model: getLLMModelLabel(generatorModel),
                durationMs: undefined,
                policies: aiInteractionTranscript.queries.find(
                  (query) => query.id === response.query_id,
                )?.outbound_policies,
              } satisfies AgentGroupItem,
              timestamp: new Date(generationStartedAt),
            })
          }

          prevResponseTime = responseTime
        }
      } else if (showDebugInfo) {
        // Single-agent: push individual tool calls into the timeline
        for (const toolCall of aiInteractionTranscript.tool_calls ?? []) {
          timeline.push({
            type: 'toolCall',
            data: toolCall as EnrichedToolCall,
            timestamp: new Date(toolCall.created),
          })
        }
      }

      timeline.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
      setTimelineItems(timeline)
    }
  }, [aiInteractionTranscript, useEnglish, showDebugInfo, showTraces])

  useEffect(() => {
    if (!scrollToMessageId) return
    const responseIndex = timelineItems.findIndex(
      (item) => item.type === 'message' && item.data.id === scrollToMessageId,
    )
    if (responseIndex === -1) return
    const precedingUserMessage = timelineItems
      .slice(0, responseIndex)
      .reverse()
      .find((item) => item.type === 'message' && item.data.sender === 'USER')
    const targetId =
      precedingUserMessage && precedingUserMessage.type === 'message'
        ? precedingUserMessage.data.id
        : scrollToMessageId
    document
      .getElementById(`interaction-log-message-${targetId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [scrollToMessageId, timelineItems])

  return (
    <div className='interaction-log'>
      {showHeader && (
        <>
          <div className='interaction-log__header'>
            <div className='interaction-log__title-row'>
              {showOrgInfo && aiInteractionTranscript?.organization_image && (
                <img
                  src={aiInteractionTranscript.organization_image}
                  alt={`${aiInteractionTranscript.organization_name || 'Organization'} logo`}
                  className='interaction-log__org-logo'
                />
              )}
              <Typography variant='h4' className='interaction-log__title' component='h1'>
                {`${showOrgInfo && aiInteractionTranscript?.organization_name ? `${aiInteractionTranscript.organization_name} ` : ''}Interaction Details`}
              </Typography>
              {aiInteractionTranscript &&
                ((): React.ReactNode => {
                  const meta = getInteractionTypeMeta(
                    aiInteractionTranscript.interaction_type ?? '',
                  )
                  const isVoiceAI =
                    aiInteractionTranscript.interaction_type === AIInteractionType.enum.VOICE
                  const iconName = meta.icon === 'Phone' ? 'CallOutlined' : 'ChatBubbleOutlined'
                  let typeLabel = meta.label || 'Chat'
                  if (isVoiceAI && isSuper) {
                    typeLabel = `Voice: ${getPhoneLineTypeDisplay(
                      (aiInteractionTranscript.phone_line_type as AIPhoneLineTypeEnum) || 'STABLE',
                    )}`
                  }
                  return (
                    <Chip
                      icon={<Icon name={iconName} size={typography.fontSizePx.sm} />}
                      label={typeLabel}
                      size='small'
                      className='interaction-log__type-chip'
                    />
                  )
                })()}
              {showDebugInfo && aiInteractionTranscript?.llm_model && (
                <Chip
                  label={
                    aiInteractionTranscript.retrieval_model ||
                    aiInteractionTranscript.generator_model ? (
                      getLLMProviderLabel(aiInteractionTranscript.llm_model)
                    ) : (
                      <span>
                        <strong>{getLLMModelLabel(aiInteractionTranscript.llm_model)}</strong>
                        <span className='interaction-log__llm-chip-provider'>
                          {' '}
                          ({getLLMProviderLabel(aiInteractionTranscript.llm_model)})
                        </span>
                      </span>
                    )
                  }
                  variant='filled'
                  size='small'
                  className='interaction-log__llm-chip'
                />
              )}
              {showDebugInfo &&
                (!!aiInteractionTranscript?.retrieval_model ||
                  !!aiInteractionTranscript?.generator_model) && (
                  <Chip
                    icon={<Icon name='AccountTree' size={typography.fontSizePx.sm} />}
                    label='Multi-Agent'
                    size='small'
                    color='purple'
                  />
                )}
              {showDebugInfo && aiInteractionTranscript?.used_granular_retrieval && (
                <Chip
                  icon={<Icon name='search' size={typography.fontSizePx.sm} />}
                  label='Granular Retrieval'
                  size='small'
                  color='teal'
                />
              )}
              {isSuper && isArchived && (
                <Chip
                  icon={<Icon name='science' size={typography.fontSizePx.sm} />}
                  label='Test'
                  size='small'
                  color='orange'
                />
              )}
            </div>
            {onClose && (
              <IconButton onClick={handleOnClose}>
                <Icon name='Close' size={typography.fontSizePx.xl} />
              </IconButton>
            )}
          </div>

          <div className='interaction-log__metadata'>
            {aiInteractionTranscript?.contact_handle && orgHandleFromUrl ? (
              <Chip
                icon={<Icon name='PersonOutlined' size={typography.fontSizePx.sm} />}
                deleteIcon={
                  resolvedContactUrl ? (
                    <Icon name='OpenInNew' size={typography.fontSizePx.xs} />
                  ) : undefined
                }
                label={aiInteractionTranscript.contact_identity_name ?? 'Contact'}
                size='small'
                onClick={
                  resolvedContactUrl
                    ? (): void => {
                        window.open(resolvedContactUrl, '_blank')
                      }
                    : undefined
                }
                onDelete={
                  resolvedContactUrl
                    ? (): void => {
                        window.open(resolvedContactUrl, '_blank')
                      }
                    : undefined
                }
                variant='outlined'
                color={resolvedContactUrl ? 'primary' : 'neutral'}
              />
            ) : (
              aiInteractionTranscript?.caller_id && (
                <Chip
                  icon={<Icon name='PersonOutlined' size={typography.fontSizePx.sm} />}
                  label={formatPhoneNumber(aiInteractionTranscript.caller_id)}
                  size='small'
                  variant='outlined'
                />
              )
            )}
            {showDebugInfo &&
              aiInteractionTranscript?.interaction_type === AIInteractionType.enum.VOICE &&
              aiInteractionTranscript?.voice_call_log && (
                <>
                  <FromToPhoneChip
                    fromNumber={aiInteractionTranscript.voice_call_log.from_phone_number}
                    toNumber={aiInteractionTranscript.voice_call_log.to_phone_number}
                    icon={<Icon name='Phone' size={typography.fontSizePx.lg} />}
                  />
                  <Chip
                    icon={<Icon name='AccessTime' size={typography.fontSizePx.lg} />}
                    label={formatDuration(
                      aiInteractionTranscript.voice_call_log.call_duration ?? 0,
                    )}
                    size='small'
                  />
                  {aiInteractionTranscript.eleven_labs_conversation_id && (
                    <Chip
                      icon={<Icon name='Launch' size={typography.fontSizePx.lg} />}
                      label='ElevenLabs Log'
                      size='small'
                      onClick={() =>
                        window.open(
                          `https://elevenlabs.io/app/agents/history/${aiInteractionTranscript.eleven_labs_conversation_id}`,
                          '_blank',
                        )
                      }
                    />
                  )}
                  {aiInteractionTranscript.phonic_conversation_id && (
                    <Chip
                      icon={<Icon name='Launch' size={typography.fontSizePx.lg} />}
                      label='Phonic Log'
                      size='small'
                      onClick={() =>
                        window.open(
                          `https://phonic.ai/conversations/polimorphic/${IS_PROD ? 'prod' : 'staging'}/${aiInteractionTranscript.phonic_conversation_id}`,
                          '_blank',
                        )
                      }
                    />
                  )}
                  {isSuper && (
                    <Chip
                      icon={<Icon name='Description' size={typography.fontSizePx.lg} />}
                      label='System Prompt'
                      size='small'
                      onClick={() => setIsSystemPromptModalOpen(true)}
                    />
                  )}
                </>
              )}
            {showDebugInfo &&
              (aiInteractionTranscript?.interaction_type === AIInteractionType.enum.SMS ||
                aiInteractionTranscript?.interaction_type === AIInteractionType.enum.WHATSAPP) &&
              aiInteractionTranscript?.sms_text_log && (
                <span className='interaction-log__sms-chip'>
                  <FromToPhoneChip
                    fromNumber={aiInteractionTranscript.sms_text_log.from_phone_number}
                    toNumber={aiInteractionTranscript.sms_text_log.to_phone_number}
                    icon={
                      aiInteractionTranscript.interaction_type ===
                      AIInteractionType.enum.WHATSAPP ? (
                        <WhatsAppBubbleIcon size={typography.fontSizePx.md} />
                      ) : (
                        <Icon name='SmsIcon' size={typography.fontSizePx.md} />
                      )
                    }
                  />
                </span>
              )}
          </div>

          <div className='interaction-log__action-bar'>
            <div className='interaction-log__action-bar-left'>
              {isSuper && aiInteractionTranscript && (
                <Tooltip
                  title={
                    isArchived ? 'Include this log in analytics' : 'Exclude this log from analytics'
                  }
                >
                  <span>
                    <Button
                      variant='outlined'
                      size='small'
                      startIcon={<Icon name='science' />}
                      onClick={() => void handleToggleArchived()}
                      disabled={archiving}
                    >
                      {isArchived ? 'Unmark as test' : 'Mark as test'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button
                variant='outlined'
                color='primary'
                onClick={() => {
                  setIsAddTestDialogOpen(true)
                  if (user?.id && orgAiSettings?.organization_identity_id) {
                    void apiZodios
                      .ai_listScenarios({
                        user_id: user.id,
                        organization_identity_id: orgAiSettings.organization_identity_id,
                      })
                      .then((res) => {
                        setScenarioCategories(
                          Array.from(
                            new Set(
                              res.scenarios.map((s) => s.category).filter(Boolean) as string[],
                            ),
                          ).sort(),
                        )
                      })
                  }
                }}
                size='small'
                disabled={addedToTestBank}
                startIcon={
                  addedToTestBank ? <Icon name='CheckCircle' /> : <Icon name='healthCheck' />
                }
              >
                {addedToTestBank ? 'Added' : 'Add to Health Check'}
              </Button>
              {(aiInteractionTranscript?.has_redacted_content || piiRevealed) && (
                <Button
                  variant='outlined'
                  color='primary'
                  size='small'
                  startIcon={
                    piiRevealed ? <Icon name='VisibilityOff' /> : <Icon name='Visibility' />
                  }
                  onClick={piiRevealed ? handleRestoreRedactions : handleRevealRedactions}
                  loading={revealLoading}
                >
                  {piiRevealed ? 'Restore Redactions' : 'Reveal Redactions'}
                </Button>
              )}
              {aiInteractionTranscript?.case_handle ? (
                <Button
                  variant='outlined'
                  color='primary'
                  size='small'
                  startIcon={<Icon name='OpenInNew' />}
                  to={`/cases/${aiInteractionTranscript?.case_handle}`}
                  newTab
                >
                  Go to Case
                </Button>
              ) : (
                aiInteractionTranscript &&
                !loading && (
                  <Button
                    variant='contained'
                    color='primary'
                    size='small'
                    startIcon={<Icon name='OpenInNew' />}
                    onClick={() => {
                      const params = new URLSearchParams({
                        ai_interaction_id: aiInteractionId,
                      })
                      if (orgAiSettings?.organization_identity_id) {
                        params.set('organization', orgAiSettings.organization_identity_id)
                      }
                      if (aiInteractionTranscript.mass_comm_context?.name) {
                        params.set(
                          'subject',
                          `Outbound Comm Response: ${aiInteractionTranscript.mass_comm_context.name}`,
                        )
                      }
                      if (aiInteractionTranscript.contact_identity_id) {
                        params.set('applicant', aiInteractionTranscript.contact_identity_id)
                      }
                      window.open(
                        `${getBasePath()}/create-case/ViaSite?${params.toString()}`,
                        '_blank',
                      )
                    }}
                  >
                    Create a Case
                  </Button>
                )
              )}
            </div>
            <div className='interaction-log__action-bar-right'>
              <Tooltip title='Copy Link'>
                <span>
                  <IconButton
                    size='small'
                    onClick={() => {
                      navigator.clipboard
                        .writeText(getShareableLink())
                        .then(() => setLinkCopied(true))
                        .catch((err) => console.error('Failed to copy URL: ', err))
                    }}
                  >
                    <Icon name='Link' size={typography.fontSizePx.xl} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Download / Share'>
                <span>
                  <IconButton size='small' onClick={handleOpenShareModal} disabled={loading}>
                    <Icon name='Download2' size={typography.fontSizePx.xl} />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </div>

          {aiInteractionTranscript && aiInteractionTranscript.audio_url && (
            <AudioPlayer audioUrl={aiInteractionTranscript.audio_url} />
          )}

          {aiInteractionTranscript?.interaction_type === AIInteractionType.enum.VOICE && (
            <div className='interaction-log__banner-area'>
              <Banner
                type='info'
                variant='standard'
                body='Transcripts are generated from the original audio with speech recognition technology. Transcript errors may occur.'
              />
            </div>
          )}
          {aiInteractionTranscript?.mass_comm_context && (
            <div className='interaction-log__banner-area'>
              <Banner
                type='feedback'
                variant='standard'
                title={`Replying to Outbound Communication${aiInteractionTranscript.mass_comm_context.name ? `: ${aiInteractionTranscript.mass_comm_context.name}` : ''}`}
                body={
                  aiInteractionTranscript.mass_comm_context.sent_at
                    ? `Sent: ${new Date(aiInteractionTranscript.mass_comm_context.sent_at).toLocaleString()}`
                    : undefined
                }
              />
            </div>
          )}
        </>
      )}
      <div className='interaction-log__chat-area'>
        {loading ? (
          <div className='interaction-log__loading'>
            <CircularProgress size={40} />
            <Typography variant='body1'>Loading interaction...</Typography>
          </div>
        ) : (
          <>
            <AIInteractionLogEvaluation
              evaluation={aiInteractionTranscript?.ai_interaction_evaluation}
            />
            <ThemeProvider theme={internalLightTheme}>
              {timelineItems.length > 0 ? (
                timelineItems.map((item, index) => (
                  <div
                    key={`${item.type}-${index}`}
                    id={
                      item.type === 'message' && item.data.id
                        ? `interaction-log-message-${item.data.id}`
                        : undefined
                    }
                    className={[
                      'interaction-log__timeline-item',
                      item.type === 'message' && item.data.sender === 'USER'
                        ? 'interaction-log__timeline-item--right'
                        : item.type === 'toolCall' ||
                            item.type === 'agentGroup' ||
                            item.type === 'agentTrace' ||
                            item.type === 'externalApiFailure' ||
                            (item.type === 'message' &&
                              item.data.sender === 'ACTION' &&
                              item.data.actionType !== 'SEARCH_RESULTS')
                          ? 'interaction-log__timeline-item--center'
                          : 'interaction-log__timeline-item--left',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <Box className='interaction-log__timestamp'>
                      {item.timestamp.toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}{' '}
                      at{' '}
                      {item.timestamp.toLocaleString('en-US', {
                        hour: 'numeric',
                        minute: 'numeric',
                        second: 'numeric',
                        hour12: true,
                      })}
                    </Box>
                    {item.type === 'message' && orgAiSettings && (
                      <MessageBubble
                        message={item.data as IAIInteractionMessageDisplay}
                        orgAiSettings={orgAiSettings}
                        isLog={true}
                        showDebugInfo={showDebugInfo}
                        renderCharts={
                          aiInteractionTranscript?.interaction_type ===
                          AIInteractionType.enum.ASSISTANT
                        }
                        isAssistantLog={
                          aiInteractionTranscript?.interaction_type ===
                          AIInteractionType.enum.ASSISTANT
                        }
                      />
                    )}
                    {showDebugInfo && item.type === 'toolCall' && (
                      <ToolCallInfo
                        orgAiSettings={orgAiSettings}
                        toolCall={item.data as EnrichedToolCall}
                      />
                    )}
                    {showDebugInfo && item.type === 'agentGroup' && (
                      <AgentToolCallDrawer
                        group={item.data as AgentGroupItem}
                        orgAiSettings={orgAiSettings}
                      />
                    )}
                    {item.type === 'externalApiFailure' && (
                      <div className='interaction-log__external-api-failure'>
                        <Chip
                          icon={<Icon name='hub' color={lightTokens.error.main} />}
                          label={item.data}
                          size='medium'
                        />
                      </div>
                    )}
                    {showTraces && item.type === 'agentTrace' && (
                      <AgentTracePanel nodes={item.data} />
                    )}
                  </div>
                ))
              ) : (
                <div className='interaction-log__empty'>
                  <Typography>No conversation data available</Typography>
                </div>
              )}
            </ThemeProvider>
          </>
        )}
      </div>
      {isSuper && debugToggleMode !== 'off' && (
        <div
          className={[
            'interaction-log__debug-toggle',
            debugToggleMode === 'floating'
              ? 'interaction-log__debug-toggle--floating'
              : 'interaction-log__debug-toggle--inline',
          ].join(' ')}
        >
          <Switch
            size='small'
            checked={debugVisible}
            onChange={(e) => setDebugVisible(e.target.checked)}
            color='primary'
            label='Debug Info'
            containerSx={{ margin: 0 }}
          />
          {hasAgentTrace && (
            <Switch
              size='small'
              checked={tracesVisible}
              onChange={(e) => setTracesVisible(e.target.checked)}
              color='primary'
              label='View Traces'
              containerSx={{ margin: 0 }}
            />
          )}
        </div>
      )}
      {hasNonEnglishContent && !loading && (
        <div className='interaction-log__language-toggle'>
          <ToggleButtonGroup
            value={useEnglish}
            exclusive
            onChange={handleLanguageChange}
            size='small'
          >
            <Tooltip
              title='This is the conversation the constituent saw'
              placement='top'
              enterDelay={400}
            >
              <ToggleButton value={false}>
                <Icon
                  name='Language'
                  size={typography.fontSizePx.md}
                  className='interaction-log__toggle-icon'
                />
                Original
              </ToggleButton>
            </Tooltip>
            <Tooltip title='Conversation translated to English' placement='top' enterDelay={400}>
              <ToggleButton value={true}>
                <Icon
                  name='TextFormat'
                  size={typography.fontSizePx.md}
                  className='interaction-log__toggle-icon'
                />
                English
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </div>
      )}
      <EditQuestionsDialog
        open={isAddTestDialogOpen}
        onClose={() => setIsAddTestDialogOpen(false)}
        category={null}
        existingCategories={scenarioCategories}
        items={(aiInteractionTranscript?.queries ?? []).map((q) => ({
          queryId: '',
          question: q.body,
          expectedAnswer: '',
          relevantSources: [],
        }))}
        onSave={async ({ category, queries }) => {
          await handleAddTest(queries, category)
        }}
      />
      <ShareLogModal
        isOpen={isShareModalOpen}
        onClose={handleCloseShareModal}
        aiInteractionId={aiInteractionId}
        transcript={aiInteractionTranscript}
        orgAiSettings={orgAiSettings}
      />
      {isSuper && (
        <SystemPromptModal
          isOpen={isSystemPromptModalOpen}
          onClose={() => setIsSystemPromptModalOpen(false)}
          aiInteractionId={aiInteractionId}
        />
      )}
      <RevealPIIModal
        isOpen={isRevealPIIModalOpen}
        onClose={() => setIsRevealPIIModalOpen(false)}
        onConfirm={handleConfirmRevealPII}
        loading={revealLoading}
      />
      <Snackbar open={linkCopied} autoHideDuration={5000} onClose={handleCloseAlert}>
        <Alert
          icon={<Icon name='CheckCircle' size={typography.fontSizePx.sm} />}
          onClose={handleCloseAlert}
          severity='success'
          className='interaction-log__alert'
        >
          Shareable log link copied
        </Alert>
      </Snackbar>
      <Snackbar open={showAddedSuccess} autoHideDuration={5000} onClose={handleCloseAlert}>
        <Alert
          icon={<Icon name='CheckCircle' size={typography.fontSizePx.sm} />}
          onClose={handleCloseAlert}
          severity='success'
          className='interaction-log__alert'
        >
          Successfully added to Health Check question bank.{' '}
          <Link
            href={location.pathname.replace(/ai-log.*$/, 'health-check')}
            target='_blank'
            rel='noopener'
            className='interaction-log__alert-link'
          >
            View in Health Check
          </Link>
        </Alert>
      </Snackbar>
      <Snackbar
        open={revealError !== null}
        autoHideDuration={6000}
        onClose={() => setRevealError(null)}
      >
        <Alert
          onClose={() => setRevealError(null)}
          severity='error'
          className='interaction-log__alert'
        >
          {revealError}
        </Alert>
      </Snackbar>
    </div>
  )
}

export default AIInteractionLogContent
